import { isIOSNewVersion } from "./util";

/**
 * iOS 26 "Liquid Glass" fixes.
 *
 * The floating bottom nav (~83px) introduced by iOS 26 overlays app content
 * and causes three problems that this module addresses at the DOM level:
 *  - Issue #1: Body scroll rubber-bands over content hidden behind the nav or
 *              bottom sticky page sections. Ionic ion-router-outlet also needs
 *              safe-area padding so bottom CTAs remain tappable above the nav.
 *  - Issue #2: Page-level bottom-anchored buttons/banners (position:fixed or
 *              position:absolute) overlap the nav.
 *  - Issue #3: VDS Modal button rows overlap the nav.
 *  - Issue #4: The webview also extends under the translucent native header,
 *              so a full-screen VDS modal loses its close bar / title behind
 *              it. Insetting the dialog keeps every modal region visible.
 *
 * All adjustments are gated by common.isIOSNewVersion() and are wired into
 * library.init so every consuming app benefits automatically after calling
 * init(). Init is idempotent — repeat calls (e.g., on route change) are safe.
 */

const FLOATING_NAV_OFFSET_PX = 85;
const MODAL_BUTTON_MARGIN_PX = 100;
const BOTTOM_ANCHOR_TOLERANCE_PX = 30;
const MAX_STICKY_BOTTOM_SECTION_HEIGHT_PX = 150;
// A sticky bar / banner never covers most of the screen; anything taller is an
// overlay (modal scrim, drawer, full-screen portal) and must not be shifted.
const MAX_STICKY_SECTION_VIEWPORT_RATIO = 0.6;
const NATIVE_HEADER_HEIGHT_PX = 50;
const MIN_MODAL_TOP_INSET_PX = 20;

const VDS_MODAL_CLASS_TOKEN = "VDS__MON__modal-module";
const VDS_MODAL_BODY_CLASS_TOKEN = "VDS__MON__modal-body-module";
const VDS_MODAL_FOOTER_CLASS_TOKEN = "VDS__MON__modal-footer-module";
const VDS_MODAL_SELECTOR = `[class*="${VDS_MODAL_CLASS_TOKEN}"]`;

// Legacy @vds/modals (styled-components): ModalContainer wraps a fixed Overlay
// that must receive the bottom offset — adjusting ModalContainer itself has no effect.
const LEGACY_VDS_MODAL_CONTAINER_TOKEN = "ModalContainer-sc-";
const LEGACY_VDS_MODAL_OVERLAY_TOKEN = "Overlay-sc-";
const LEGACY_VDS_MODAL_SELECTOR = `[class*="${LEGACY_VDS_MODAL_CONTAINER_TOKEN}"]`;
const ANY_VDS_MODAL_SELECTOR = `${VDS_MODAL_SELECTOR}, ${LEGACY_VDS_MODAL_SELECTOR}`;
// Styled-components class hashes change between VDS builds, so modal chrome is
// also matched by ARIA — this catches overlays/containers of any modal build.
const MODAL_ROOT_SELECTOR = "[role=\"dialog\"], [aria-modal=\"true\"]";

// Issue #4 targets the dialog itself rather than the container / overlay.
const LEGACY_VDS_MODAL_DIALOG_TOKEN = "ModalDialog";
const LEGACY_VDS_MODAL_CLOSE_BUTTON_TOKEN = "ModalCloseButton";
const LEGACY_VDS_MODAL_FOOTER_TOKEN = "ModalFooter";
const MODAL_FOOTER_SELECTOR = `footer, [class*="${VDS_MODAL_FOOTER_CLASS_TOKEN}"], [class*="${LEGACY_VDS_MODAL_FOOTER_TOKEN}"]`;

// Modals render at the end of <body>, so the scoped styles hang off the document root.
const ROOT_CLASS = "vz-ios-liquid-glass";
const STYLE_ELEMENT_ID = "vz-ios-liquid-glass-style";

const FIXED_ADJUSTED_ATTR = "iosLgFixedBottomAdjusted";
const MODAL_BTN_ADJUSTED_ATTR = "iosLgModalButtonAdjusted";
const LEGACY_MODAL_OVERLAY_ADJUSTED_ATTR = "iosLgLegacyModalOverlayAdjusted";
const INIT_FLAG_ATTR = "iosLgInitialized";
const ION_ROUTER_OUTLET_ADJUSTED_ATTR = "iosLgIonRouterOutletAdjusted";
const ION_ROUTER_OUTLET_SELECTOR = "ion-router-outlet";
const SAFE_AREA_PADDING_BOTTOM = "env(safe-area-inset-bottom)";

const MODAL_ACTION_SELECTOR = "button, a";
const BOTTOM_ACTION_SELECTOR = "button, a, [role=\"button\"]";
const BOTTOM_STICKY_SECTION_TAGS = new Set(["DIV", "FOOTER"]);

const hasClassStartingWith = (element, token) => {
    const className = element && element.className;
    if (typeof className !== "string" || className.length === 0) return false;
    return className.split(/\s+/).some((cls) => cls.startsWith(token));
};

const isModalCloseAction = (element) =>
    element.dataset.testid === "modal-close-button";

const parseCssPx = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Returns the modal's trailing bottom action (button or link), excluding the
 * header close icon. When content scrolls, falls back to the lowest action in
 * the modal's content flow so footer CTAs still receive the nav offset.
 */
const findLastBottomModalAction = (modal) => {
    const candidates = Array.from(modal.querySelectorAll(MODAL_ACTION_SELECTOR))
        .filter((el) => el instanceof HTMLElement)
        .filter((el) => !isModalCloseAction(el));

    if (candidates.length === 0) return null;

    const modalRect = modal.getBoundingClientRect();
    const bottomAnchored = candidates.filter((el) => {
        const rect = el.getBoundingClientRect();
        return modalRect.bottom - rect.bottom <= BOTTOM_ANCHOR_TOLERANCE_PX;
    });

    if (bottomAnchored.length > 0) {
        return bottomAnchored[bottomAnchored.length - 1];
    }

    if (modal.scrollHeight > modal.clientHeight) {
        return candidates.reduce((lowest, el) =>
            el.getBoundingClientRect().bottom > lowest.getBoundingClientRect().bottom ? el : lowest,
        );
    }

    return null;
};

const isVDSModalNode = (element) => {
    if (!element || !(element instanceof HTMLElement)) return false;
    return hasClassStartingWith(element, VDS_MODAL_CLASS_TOKEN);
};

const isLegacyVDSModalNode = (element) => {
    if (!element || !(element instanceof HTMLElement)) return false;
    return hasClassStartingWith(element, LEGACY_VDS_MODAL_CONTAINER_TOKEN);
};

/**
 * True for a modal dialog, anything inside one, and the scrim / container
 * wrappers that hold one. Those are owned by Issues #3 and #4 and must never
 * be treated as a page-level sticky bottom section.
 */
const isModalChrome = (element) => {
    if (!element || !element.closest || !element.querySelector) return false;
    return !!element.closest(ANY_VDS_MODAL_SELECTOR)
        || !!element.closest(MODAL_ROOT_SELECTOR)
        || !!element.querySelector(MODAL_ROOT_SELECTOR);
};

const findLegacyModalOverlay = (modalContainer) => {
    const overlaySelector = `div[class*="${LEGACY_VDS_MODAL_OVERLAY_TOKEN}"]`;
    const overlay = modalContainer.querySelector(overlaySelector);
    return overlay instanceof HTMLElement ? overlay : null;
};

/**
 * A footer pinned to the dialog's bottom edge (position:sticky / fixed) sticks
 * to the padding box, so the Issue #4 bottom inset cannot push it clear of the
 * floating nav — only shrinking the overlay can.
 */
const hasBottomPinnedFooter = (modalContainer) =>
    Array.from(modalContainer.querySelectorAll(MODAL_FOOTER_SELECTOR)).some((el) => {
        if (!(el instanceof HTMLElement)) return false;

        const computed = window.getComputedStyle(el);
        if (computed.position !== "sticky" && computed.position !== "fixed") return false;

        const bottom = parseCssPx(computed.bottom);
        return bottom !== null && bottom <= BOTTOM_ANCHOR_TOLERANCE_PX;
    });

/**
 * Issue #3 (legacy @vds/modals): shrink the fixed Overlay inside
 * ModalContainer so sticky modal-footer CTAs clear the floating nav.
 * ModalContainer itself is full-viewport fixed; only its Overlay child
 * responds to a bottom offset.
 *
 * Modals whose buttons sit in normal content flow are left alone — shrinking
 * their overlay stacks an extra 85px gap on top of the Issue #4 bottom inset.
 */
const adjustLegacyVDSModalOverlay = (modalContainer) => {
    if (!(modalContainer instanceof HTMLElement)) return;
    if (!isLegacyVDSModalNode(modalContainer)) return;

    const overlay = findLegacyModalOverlay(modalContainer);
    if (!overlay || overlay.dataset[LEGACY_MODAL_OVERLAY_ADJUSTED_ATTR] === "true") return;

    // Defer measurement to the next frame so the modal's enter animation /
    // layout has settled before we sample the footer's position.
    window.requestAnimationFrame(() => {
        if (!overlay.isConnected) return;
        if (overlay.dataset[LEGACY_MODAL_OVERLAY_ADJUSTED_ATTR] === "true") return;
        if (!hasBottomPinnedFooter(modalContainer)) return;

        overlay.dataset[LEGACY_MODAL_OVERLAY_ADJUSTED_ATTR] = "true";
        overlay.style.bottom = `${FLOATING_NAV_OFFSET_PX}px`;
    });
};

const scanLegacyVDSModals = (root) => {
    root.querySelectorAll(LEGACY_VDS_MODAL_SELECTOR).forEach(adjustLegacyVDSModalOverlay);
};

/** Accepts either a bare number ("120") or a full CSS length ("120px", "8rem"). */
const toCssLength = (value) => {
    const trimmed = typeof value === "string" ? value.trim() : "";
    if (!trimmed) return null;
    return /^-?\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
};

const resolveFooterBottom = (footerBottomPx) =>
    toCssLength(footerBottomPx) || `${FLOATING_NAV_OFFSET_PX}px`;

/**
 * Issue #2: Push "sticky" bottom-anchored elements (buttons/banners) above
 * the floating bottom nav by setting their bottom offset. Covers both
 * position:fixed elements (anchored to the viewport) and position:absolute
 * elements whose containing block extends to the viewport bottom.
 */
const adjustBottomAnchoredElement = (element, footerBottom) => {
    if (!(element instanceof HTMLElement)) return;
    if (!BOTTOM_STICKY_SECTION_TAGS.has(element.tagName)) return;
    if (element.dataset[FIXED_ADJUSTED_ATTR] === "true") return;

    const computed = window.getComputedStyle(element);
    if (computed.position !== "fixed" && computed.position !== "absolute") return;

    // VDS Modals are also position:fixed but are handled by Issues #3 and #4.
    if (isModalChrome(element)) return;

    const isStickyFooter = element.tagName === "FOOTER";
    if (!isStickyFooter && !element.querySelector(BOTTOM_ACTION_SELECTOR)) return;

    // Only touch elements anchored to the bottom of the viewport (sticky
    // buttons / banners) — never full-height overlays or absolutely
    // positioned elements that live somewhere in the middle of the page.
    const rect = element.getBoundingClientRect();
    if (rect.height > window.innerHeight * MAX_STICKY_SECTION_VIEWPORT_RATIO) return;

    const bottomOffset = parseFloat(computed.bottom);
    const isCssBottomAnchored =
        Number.isFinite(bottomOffset) && bottomOffset <= BOTTOM_ANCHOR_TOLERANCE_PX;
    const distanceFromViewportBottom = window.innerHeight - rect.bottom;
    const isRectBottomAnchored =
        distanceFromViewportBottom <= BOTTOM_ANCHOR_TOLERANCE_PX;
    if (!isCssBottomAnchored && !isRectBottomAnchored) return;

    // footerBottom is always produced by resolveFooterBottom(), which never
    // returns a falsy value, so this fallback can't be exercised through the
    // public init() call path — kept as defensive coding and exercised
    // directly in tests via the __testInternals__ export below.
    const resolvedFooterBottom = footerBottom || `${FLOATING_NAV_OFFSET_PX}px`;

    const el = element;
    el.dataset[FIXED_ADJUSTED_ATTR] = "true";
    el.style.bottom = resolvedFooterBottom;
};

const scanBottomAnchoredElements = (root, footerBottom) => {
    root.querySelectorAll("div, footer").forEach((element) =>
        adjustBottomAnchoredElement(element, footerBottom),
    );
};

const scanAncestorBottomAnchoredElements = (node, footerBottom) => {
    if (!(node instanceof HTMLElement)) return;

    let ancestor = node.parentElement;
    while (ancestor) {
        if (BOTTOM_STICKY_SECTION_TAGS.has(ancestor.tagName)) {
            adjustBottomAnchoredElement(ancestor, footerBottom);
        }
        ancestor = ancestor.parentElement;
    }
};

/**
 * Issue #3 margin target: prefer the scrollable modal body so header chrome
 * (close icon, top spacing) is unaffected. Falls back to the last non-sticky
 * div inside the modal — never the modal root itself.
 */
const isNonStickyDiv = (element) => {
    const { position } = window.getComputedStyle(element);
    return position !== "sticky" && position !== "fixed" && position !== "absolute";
};

const findModalMarginTarget = (modal) => {
    const modalBody = modal.querySelector(`div[class*="${VDS_MODAL_BODY_CLASS_TOKEN}"]`);
    if (modalBody instanceof HTMLElement) return modalBody;

    const divs = Array.from(modal.querySelectorAll("div")).filter(
        (el) =>
            el instanceof HTMLElement &&
            el !== modal &&
            !hasClassStartingWith(el, VDS_MODAL_CLASS_TOKEN) &&
            isNonStickyDiv(el),
    );

    return divs.length > 0 ? divs[divs.length - 1] : null;
};

/**
 * Issue #3: When a VDS Modal opens, add a bottom margin so its trailing
 * button (or the button-row wrapping it) clears the floating nav. Only
 * applies when the modal actually overlaps the nav's danger zone or its
 * content overflows the viewport — a short, centered modal that never
 * touches the nav is left untouched.
 */
const adjustVDSModalNode = (modal) => {
    if (!(modal instanceof HTMLElement)) return;

    const marginTarget = findModalMarginTarget(modal);
    if (!marginTarget) return;
    if (marginTarget.dataset[MODAL_BTN_ADJUSTED_ATTR] === "true") return;

    const lastAction = findLastBottomModalAction(modal);
    if (!lastAction) return;

    // Defer measurement to the next frame so the modal's enter animation /
    // layout has settled before we sample element positions.
    window.requestAnimationFrame(() => {
        if (!lastAction.isConnected) return;
        if (!marginTarget.isConnected) return;
        if (marginTarget.dataset[MODAL_BTN_ADJUSTED_ATTR] === "true") return;

        const rect = lastAction.getBoundingClientRect();
        const overlapsFloatingNav =
            rect.bottom > window.innerHeight - FLOATING_NAV_OFFSET_PX;
        const modalContentOverflows = modal.scrollHeight > modal.clientHeight;

        if (!overlapsFloatingNav && !modalContentOverflows) return;

        marginTarget.dataset[MODAL_BTN_ADJUSTED_ATTR] = "true";
        marginTarget.style.marginBottom = `${MODAL_BUTTON_MARGIN_PX}px`;
    });
};

const scanVDSModals = (root) => {
    root.querySelectorAll(VDS_MODAL_SELECTOR).forEach(adjustVDSModalNode);
};

const isBottomStickySection = (element) => {
    if (!BOTTOM_STICKY_SECTION_TAGS.has(element.tagName)) return false;
    if (isModalChrome(element)) return false;
    if (element.getBoundingClientRect().height >
        window.innerHeight * MAX_STICKY_SECTION_VIEWPORT_RATIO) return false;

    const computed = window.getComputedStyle(element);
    const isBottomAnchored = computed.position === "sticky" || computed.position === "fixed";
    if (!isBottomAnchored) return false;

    // Issue #2 shifts fixed footers to bottom: 85px — still count them for padding.
    if (element.dataset[FIXED_ADJUSTED_ATTR] === "true") return true;

    const bottom = parseCssPx(computed.bottom);
    if (bottom === null || bottom > BOTTOM_ANCHOR_TOLERANCE_PX) return false;

    const top = parseCssPx(computed.top);
    if (top !== null && top <= BOTTOM_ANCHOR_TOLERANCE_PX) return false;

    return true;
};

const getStickyBottomSectionHeightPx = () => {
    let maxHeight = 0;

    document.body.querySelectorAll("div, footer").forEach((element) => {
        if (!(element instanceof HTMLElement)) return;
        if (!isBottomStickySection(element)) return;

        const height = Math.max(element.getBoundingClientRect().height, element.offsetHeight);
        if (height > maxHeight) maxHeight = height;
    });

    return Math.ceil(maxHeight);
};

const buildBodyPaddingBottom = (stickyHeightPx) => {
    const cappedHeightPx = Math.min(
        Math.max(stickyHeightPx, 0),
        MAX_STICKY_BOTTOM_SECTION_HEIGHT_PX,
    );
    if (cappedHeightPx <= 0) return SAFE_AREA_PADDING_BOTTOM;
    return `calc(${SAFE_AREA_PADDING_BOTTOM} + ${cappedHeightPx}px)`;
};

const adjustIonRouterOutlet = (element) => {
    if (!(element instanceof HTMLElement)) return;
    if (element.tagName !== "ION-ROUTER-OUTLET") return;
    if (element.dataset[ION_ROUTER_OUTLET_ADJUSTED_ATTR] === "true") return;

    const el = element;
    el.dataset[ION_ROUTER_OUTLET_ADJUSTED_ATTR] = "true";
    el.style.paddingBottom = SAFE_AREA_PADDING_BOTTOM;
};

const scanIonRouterOutlets = (root) => {
    if (root instanceof HTMLElement && root.tagName === "ION-ROUTER-OUTLET") {
        adjustIonRouterOutlet(root);
    }
    if (root.querySelectorAll) {
        root.querySelectorAll(ION_ROUTER_OUTLET_SELECTOR).forEach(adjustIonRouterOutlet);
    }
};

const applyBodyPadding = () => {
    // Issue #1: prevent the page from bouncing back over content that sits
    // behind the floating bottom nav / home indicator, plus any bottom-sticky
    // page sections (div or footer) that reduce the reachable scroll range.
    document.body.style.paddingBottom = buildBodyPaddingBottom(getStickyBottomSectionHeightPx());
};

const applySafeAreaPadding = () => {
    applyBodyPadding();
    scanIonRouterOutlets(document.body);
};

const processAddedNode = (node, footerBottom) => {
    if (!(node instanceof HTMLElement)) return;

    if (isVDSModalNode(node)) adjustVDSModalNode(node);
    if (isLegacyVDSModalNode(node)) adjustLegacyVDSModalOverlay(node);
    if (BOTTOM_STICKY_SECTION_TAGS.has(node.tagName)) {
        adjustBottomAnchoredElement(node, footerBottom);
    }

    // Child content of an already-open modal may mount later (async data,
    // form reveals, etc.). Re-check the enclosing modal in that case.
    const enclosingModal = node.closest && node.closest(VDS_MODAL_SELECTOR);
    if (enclosingModal) adjustVDSModalNode(enclosingModal);

    const enclosingLegacyModal = node.closest && node.closest(LEGACY_VDS_MODAL_SELECTOR);
    if (enclosingLegacyModal) adjustLegacyVDSModalOverlay(enclosingLegacyModal);

    scanBottomAnchoredElements(node, footerBottom);
    scanAncestorBottomAnchoredElements(node, footerBottom);
    scanVDSModals(node);
    scanLegacyVDSModals(node);
    applySafeAreaPadding();
};

let mutationObserver = null;

const VIEWPORT_META_DEFAULT_CONTENT =
    "width=device-width, initial-scale=1.0, viewport-fit=cover";
const VIEWPORT_FIT_COVER = "viewport-fit=cover";

/**
 * `env(safe-area-inset-bottom)` only resolves to a non-zero value when the
 * document opts into the safe-area insets via `viewport-fit=cover` on its
 * viewport meta tag. Add / patch the tag so the Issue #1 padding actually
 * takes effect on iOS 26 devices.
 */
const ensureViewportMetaTag = () => {
    const existingMeta = document.querySelector('meta[name="viewport"]');

    if (!existingMeta) {
        const meta = document.createElement("meta");
        meta.name = "viewport";
        meta.content = VIEWPORT_META_DEFAULT_CONTENT;
        document.head.appendChild(meta);
        return;
    }

    const currentContent = (existingMeta.getAttribute("content") || "").trim();
    if (currentContent.indexOf(VIEWPORT_FIT_COVER) !== -1) return;

    let nextContent;
    if (currentContent.length === 0) {
        nextContent = VIEWPORT_FIT_COVER;
    } else if (currentContent.endsWith(",")) {
        nextContent = `${currentContent} ${VIEWPORT_FIT_COVER}`;
    } else {
        nextContent = `${currentContent}, ${VIEWPORT_FIT_COVER}`;
    }

    existingMeta.setAttribute("content", nextContent);
};

// Used when the consuming app does not supply its own inset / margin.
const DEFAULT_MODAL_TOP_INSET = `max(calc(env(safe-area-inset-top, 0px) + ${NATIVE_HEADER_HEIGHT_PX}px), ${MIN_MODAL_TOP_INSET_PX}px)`;
// The dialog's own bottom inset is what lifts in-flow content above the
// floating nav, so it must never reuse the (taller) top inset.
const DEFAULT_MODAL_BOTTOM_INSET = `max(env(safe-area-inset-bottom, 0px), ${FLOATING_NAV_OFFSET_PX}px)`;
const DEFAULT_MODAL_FOOTER_MARGIN = `${FLOATING_NAV_OFFSET_PX}px`;

/**
 * Issue #4: Inset the modal dialog itself so its header (close bar / title)
 * clears the native header and its footer clears the tab bar. Covers both VDS
 * modal builds: CSS-module (`VDS__MON__modal-module`) and the legacy
 * styled-components `ModalDialog`.
 *
 * No media query is needed — isIOSNewVersion() already restricts this to the
 * iPhone My Verizon app.
 */
const buildLiquidGlassStyles = (topInset, bottomInset, footerMargin) => `
.${ROOT_CLASS} [class*="${VDS_MODAL_CLASS_TOKEN}"][role="dialog"],
.${ROOT_CLASS} [class*="${LEGACY_VDS_MODAL_DIALOG_TOKEN}"][role="dialog"] {
  box-sizing: border-box;
  padding-top: ${topInset} !important;
  padding-bottom: ${bottomInset} !important;
}

/* VDS positions the close bar absolutely at the dialog top; move it below the header. */
.${ROOT_CLASS} [class*="${VDS_MODAL_CLASS_TOKEN}"][role="dialog"] > [id$="-close-button-BgLayer"],
.${ROOT_CLASS} [class*="${LEGACY_VDS_MODAL_DIALOG_TOKEN}"][role="dialog"] > [class*="${LEGACY_VDS_MODAL_CLOSE_BUTTON_TOKEN}"] {
  top: ${topInset} !important;
}

/* Footer CTAs are nested inside the modal content container, not direct children. */
.${ROOT_CLASS} [class*="${VDS_MODAL_CLASS_TOKEN}"][role="dialog"] [class*="${VDS_MODAL_FOOTER_CLASS_TOKEN}"],
.${ROOT_CLASS} [class*="${LEGACY_VDS_MODAL_DIALOG_TOKEN}"][role="dialog"] [class*="${VDS_MODAL_FOOTER_CLASS_TOKEN}"] {
  margin-bottom: ${footerMargin} !important;
}
`;

const applyLiquidGlassStyles = (modalTopInsetPx, modalBottomMarginPx) => {
    document.documentElement.classList.add(ROOT_CLASS);

    if (document.getElementById(STYLE_ELEMENT_ID)) return;

    const style = document.createElement("style");
    style.id = STYLE_ELEMENT_ID;
    style.textContent = buildLiquidGlassStyles(
        toCssLength(modalTopInsetPx) || DEFAULT_MODAL_TOP_INSET,
        DEFAULT_MODAL_BOTTOM_INSET,
        toCssLength(modalBottomMarginPx) || DEFAULT_MODAL_FOOTER_MARGIN,
    );
    document.head.appendChild(style);
};

const iosLiquidGlass = {
    /**
     * Called from library.init. Safe to call repeatedly — the observer and
     * body-padding tweak are applied only once per document.
     *
     * @param {string} [modalTopInsetPx] Override for modal top inset
     *   (`liquidGlassModalTopInsetPx`).
     * @param {string} [modalBottomMarginPx] Override for modal footer margin
     *   (`liquidGlassModalBottomMarginPx`).
     * @param {string} [footerBottomPx] Override for sticky footer `bottom`
     *   (`liquidGlassFooterBottomPx`). Falls back to FLOATING_NAV_OFFSET_PX.
     */
    init(modalTopInsetPx, modalBottomMarginPx, footerBottomPx) {
        try {
            if (typeof window === "undefined" || typeof document === "undefined") return;
            if (!isIOSNewVersion()) return;
            if (document.body.dataset[INIT_FLAG_ATTR] === "true") return;

            document.body.dataset[INIT_FLAG_ATTR] = "true";

            const footerBottom = resolveFooterBottom(footerBottomPx);

            ensureViewportMetaTag();
            applyLiquidGlassStyles(modalTopInsetPx, modalBottomMarginPx);
            scanBottomAnchoredElements(document.body, footerBottom);
            scanVDSModals(document.body);
            scanLegacyVDSModals(document.body);
            applySafeAreaPadding();

            mutationObserver = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    mutation.addedNodes.forEach((node) => processAddedNode(node, footerBottom));
                }
            });

            mutationObserver.observe(document.body, {
                childList: true,
                subtree: true,
            });
        } catch (ex) {
            console.error("iosLiquidGlass init ERROR---", ex);
        }
    },

};

// Exposed only so tests can exercise defensive branches (e.g. a falsy
// footerBottom) that are unreachable through the public init() API.
export const __testInternals__ = { adjustBottomAnchoredElement };

export default iosLiquidGlass;
